import {TheCageGameAssets} from "../../TheCageGameAssets";
import {TheCage} from "../../TheCage";
import {gsap} from "gsap";
import {SpineGSAPWrapper} from "../../animation/SpineGSAPWrapper";
import {TheCageSound} from "../../sound/TheCageSound";

export enum FreespinsFeatureFrameStates {
    INACTIVE,
    ACTIVE,
    ACTIVE_USED,
}

export class FreespinsFeatures extends PIXI.Container {
    private frameSpine: PIXI.spine.Spine;
    private level: number;
    private usedFeatures: boolean[] = [false, false, false];
    constructor() {
        super();
        this.level = 0;

        this.position.set(360,360)
        this.frameSpine = new PIXI.spine.Spine(TheCage.assetLoader.getSpineAsset(TheCageGameAssets.FS_FEATURE_FRAME));
        this.frameSpine.position.set(0, 124)
        this.addChild(this.frameSpine);
    }

    public resetFrame(): gsap.core.Timeline {
        const tl = gsap.timeline();
        tl.add(() => {
            this.frameSpine.state.setEmptyAnimation(0, 0)
            this.frameSpine.state.setEmptyAnimation(1, 0)
            this.frameSpine.state.setEmptyAnimation(2, 0)
            this.frameSpine.state.setEmptyAnimation(3, 0)
            this.frameSpine.state.setEmptyAnimation(4, 0)
        });
        return tl;
    }

    public init(level: number): void {
        this.level = level;
    }

    public restore(level:number, featureStates: FreespinsFeatureFrameStates[]):void {
        this.init(level);
        this.idle(0);
        featureStates.forEach((featureState, i) => {
            if(featureState != FreespinsFeatureFrameStates.INACTIVE) {
                if(featureState == FreespinsFeatureFrameStates.ACTIVE) {
                    this.idle(i + 1);
                    this.usedFeatures[i] = false;
                } else {
                    this.idleInactive(i + 1);
                    this.usedFeatures[i] = true;
                }
            }
        }) ;
    }

    public idle(featureIndex: number): gsap.core.Timeline {
        const tl = gsap.timeline();
        tl.add(() => {this.frameSpine.state.addAnimation(featureIndex, `FS${featureIndex}_idle`, true, 0)});
        return tl;
    }

    public idleInactive(featureIndex: number): gsap.core.Timeline {
        const tl = gsap.timeline();
        if (featureIndex != 4) {
            tl.add(() => {this.frameSpine.state.addAnimation(featureIndex, `FS${featureIndex}_idle_inactive`, true, 0)});
        } else {
            tl.add(() => {this.frameSpine.state.addAnimation(4, `FS4_idle`, true, 0)});
        }
        return tl;
    }

    public freespinsIntro(): gsap.core.Timeline {
        this.level = 0;
        this.usedFeatures = [false, false, false];
        const tl = gsap.timeline();
        tl.add(() => {TheCage.soundPlayer.playSound(TheCageSound.FS_FRAME_APPEAR)});
        tl.add(SpineGSAPWrapper.createTimeline(this.frameSpine, 0, "FS0_intro", 0, 1, 0));
        tl.add(this.idle(0));
        return tl;
    }

    public freespinsIntroFeatureLevel3(): gsap.core.Timeline {
        this.level = 0;
        this.usedFeatures = [false, false, false];
        const tl = gsap.timeline();
        tl.add(() => {TheCage.soundPlayer.playSound(TheCageSound.FS_FRAME_APPEAR)});
        tl.add(SpineGSAPWrapper.createTimeline(this.frameSpine, 0, "FS0_intro", 0, 1, 0));
        tl.add(this.levelUp());
        tl.add(this.levelUp(), '<0.5');
        tl.add(this.idle(2));
        return tl;
    }

    public freespinsIntroFeatureLevel5(): gsap.core.Timeline {
        this.level = 0;
        this.usedFeatures = [false, false, false];
        const tl = gsap.timeline();
        tl.add(() => {TheCage.soundPlayer.playSound(TheCageSound.FS_FRAME_APPEAR)});
        tl.add(SpineGSAPWrapper.createTimeline(this.frameSpine, 0, "FS0_intro", 0, 1, 0));
        tl.add(this.levelUp());
        tl.add(this.levelUp(), '<0.3');
        tl.add(this.levelUp(), '<0.3');
        tl.add(this.levelUp(), '<0.3');
        tl.add(this.idle(4));
        return tl;
    }

    public levelUp(): gsap.core.Timeline {
        this.level++;
        const tl = gsap.timeline();
        if (this.level == 4) {
            tl.add(() => {TheCage.soundPlayer.playSound(TheCageSound.FS_FEATURE_APPEAR_4)});
        } else {
            tl.add(() => {TheCage.soundPlayer.playSound(TheCageSound.FS_FEATURE_APPEAR)});
        }
        tl.add(SpineGSAPWrapper.createTimeline(this.frameSpine, this.level, `FS${this.level}_activate`))
        tl.add(this.idle(this.level));
        return tl;
    }

    public reactive(): gsap.core.Timeline {
       const tl = gsap.timeline();
        if (this.level > 0) {
            for (let i = 1; i < this.level + 1; i++) {
                if (this.usedFeatures[i - 1]) {
                    tl.add(SpineGSAPWrapper.createTimeline(this.frameSpine, i, `FS${i}_effect_reactivate`));
                    tl.add(() => {this.idle(i)});
                    this.usedFeatures[i - 1] = false;
                }

            }
        }
       return tl;
    }

    public useMultiplier(): gsap.core.Timeline {
       const tl = gsap.timeline();
       this.usedFeatures[0] = true;
       tl.add(() => {TheCage.soundPlayer.playSound(TheCageSound.FS_MULTIPLIER)});
       tl.add(SpineGSAPWrapper.createTimeline(this.frameSpine, 1, "FS1_effect"))
       tl.add(this.idleInactive(1));
       return tl;
    }

    public useTransform1(): gsap.core.Timeline {
        const tl = gsap.timeline();
        this.usedFeatures[1] = true;
        tl.add(SpineGSAPWrapper.createTimeline(this.frameSpine, 2, "FS2_effect"));
        tl.add(this.idleInactive(2));
        return tl;
    }

    public useTransform2(): gsap.core.Timeline {
        const tl = gsap.timeline();
        this.usedFeatures[2] = true;
        tl.add(SpineGSAPWrapper.createTimeline(this.frameSpine, 3, "FS3_effect"))
        tl.add(this.idleInactive(3));
        return tl;
    }

    public useStickyWild(): gsap.core.Timeline {
        const tl = gsap.timeline();
        tl.add(SpineGSAPWrapper.createTimeline(this.frameSpine, 4, "FS4_effect"))
        tl.add(this.idle(4));
        return tl;
    }
}